import React, { useContext, useMemo, useState } from "react";

import { MoreVert } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Snackbar,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { Clipboard } from "react-feather";
import { Link, useParams } from "react-router-dom";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { UserStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { PermissionsContext } from "../../../../context/permissions-context";
import { useCommonStyles } from "../../../../styled-utils";
import { UrlParamTypes } from "../../../../types/org-display";
import ActivateDeactivateSelectedUsersModal from "../user-actions/activate-deactivate-users-modal";
import ChangeUserSeatTypeSelectedUsersModal from "../user-actions/change-user-seat-type-modal";
import RemoveUser from "../user-actions/remove-user";
import TransferUser from "../user-actions/transfer-user";
import PermissionsDataGrid from "./permissions-data-grid";
import {
  promoteUserToAdmin,
  removeUserFromOrg,
  transferMultiOrgUser,
} from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import UserOrgsCountChip from "components/shared/chip/user-orgs-count-chip";
import ConfirmDialog from "components/shared/confirm-dialog";
import GenericDialog from "components/shared/generic-dialog";
import { subscriptionSeatTypesMapping } from "constants/seats";
import { OrgDataContext } from "context/organisation-context";
import {
  AdminOnlyFilterContext,
  UserStatusFilterContext,
} from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { showPrivilegeIcons } from "helpers/privileges";
import { getMigrationRegion } from "helpers/region";
import { toUUID } from "helpers/s12-helpers";
import { convertTimestampToDate } from "helpers/time";
import { isEmaillessUser } from "helpers/user";
import { truncateString } from "helpers/utils";

interface UserRecordWrapperProps {
  row: User;
  handleRefreshUserList: () => void;
  handleRefreshOrgDisplay: () => void;
  isPremiumOrg: boolean;
  isRemoveUserDialogueDisabled: boolean;
  selected: boolean;
  setSelected: (newSelected: boolean) => void;
}

const UserRecord = ({
  row,
  handleRefreshUserList,
  handleRefreshOrgDisplay,
  isPremiumOrg,
  isRemoveUserDialogueDisabled,
  selected,
  setSelected,
}: UserRecordWrapperProps) => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { orgData } = useContext(OrgDataContext);

  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [errorState, setErrorState] = useState(false);
  const [failedUsers, setFailedUsers] = useState<string[]>([]);
  const [openRemoveUserDialog, setRemoveUserDialog] = useState(false);
  const [openTransferUserDialog, setTransferUserDialog] = useState(false);
  const [changeSeatTypeDialog, setChangeSeatTypeDialog] = useState(false);
  const [openPermissionSetsDialog, setOpenPermissionSetsDialog] =
    useState(false);
  const [
    promoteToAdminViaPermissionsDialog,
    setPromoteToAdminViaPermissionsDialog,
  ] = useState(false);
  const [promotedToAdminSnackbar, setPromotedToAdminSnackbar] = useState(false);
  const [activateDeactivateDialog, setActivateDeactivateDialog] =
    useState(false);

  const [assigneeId, setAssigneeId] = useState("");
  const [targetOrgId, setTargetOrgId] = useState("");
  const [targetSeatType, setTargetSeatType] = useState(
    SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED,
  );
  const [disableRemoveUserConfirmBtn, setDisableRemoveUserConfirmBtn] =
    useState(true);
  const [disableTransferUserConfirmBtn, setDisableTransferUserConfirmBtn] =
    useState(true);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const { userStatusFilter } = useContext(UserStatusFilterContext);
  const { adminOnlyFilter } = useContext(AdminOnlyFilterContext);
  const [actionMenuAnchor, setActionMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const styles = useCommonStyles();

  const subscription = useMemo(() => {
    return orgData?.subscription && orgData?.subscription[0];
  }, [orgData]);

  const isSubActive = subscription?.active || false;

  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const handleRemoveUserDialog = () => {
    setActionMenuAnchor(null);
    setRemoveUserDialog(true);
  };

  const handleTransferUserDialog = () => {
    setActionMenuAnchor(null);
    setTransferUserDialog(true);
  };

  const handlePromoteToAdminViaPermissionsDialog = () => {
    setActionMenuAnchor(null);
    setPromoteToAdminViaPermissionsDialog(true);
  };

  const handleActivateDeactivateDialog = () => {
    setActionMenuAnchor(null);
    setActivateDeactivateDialog(true);
  };

  const handleChangeSeatTypeDialog = () => {
    setActionMenuAnchor(null);
    setChangeSeatTypeDialog(true);
  };

  const handleRemoveUser = (
    userId: string,
    roleId: string,
    assigneeId: string,
  ) => {
    removeUserFromOrg(
      {
        userId,
        roleId,
        assigneeId,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        if (res) {
          handleRefreshUserList();
          handleRefreshOrgDisplay();
        }
      })
      .catch(showError);
  };

  const handleTransferUser = (
    sourceUserId: string,
    sourceOrgId: string,
    sourceRegion: string,
    sourceUserEmail: string,
    targetOrgId: string,
    targetSeatType: SubscriptionSeatTypes,
  ) => {
    setShowSkeleton(true);

    const migrationRegion = getMigrationRegion(sourceRegion);
    transferMultiOrgUser(
      {
        sourceUserId,
        sourceOrgId,
        sourceRegion: migrationRegion,
        sourceUserEmail,
        targetOrgId,
        targetSeatType,
      },
      callOptionsHeaders(sourceRegion, roleId),
    )
      .then((res) => {
        if (res) {
          handleRefreshUserList();
          handleRefreshOrgDisplay();
        }
      })
      .catch(showError)
      .finally(() => {
        setShowSkeleton(false);
      });
  };

  const promoteUserToAdminViaPermissions = () => {
    promoteUserToAdmin(
      {
        orgId: toUUID(roleId),
        userId: toUUID(row.id),
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setPromotedToAdminSnackbar(true);
        handleRefreshUserList();
      })
      .catch(showError);
  };

  const userLastSeenDisplay = (lastSeen: Date | undefined) => {
    if (!lastSeen) {
      return "-";
    }
    const now = DateTime.now();
    const lastSeenDate = DateTime.fromJSDate(lastSeen);
    const duration = now.diff(lastSeenDate, "seconds").seconds;

    if (duration < 1200) {
      return (
        <Typography
          variant="subtitle1"
          display="inline"
          style={{ color: "#0AAD69" }}
        >
          Online now
        </Typography>
      );
    }

    return lastSeenDate.toRelative();
  };

  const handleActionMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };

  const renderSkeletonRow = () => {
    return (
      <>
        <TableCell
          align="left"
          style={{ verticalAlign: "top", paddingLeft: "30px" }}
        >
          <Skeleton
            variant="text"
            width="120px"
            height="20px"
            animation="wave"
          />
          <Skeleton
            variant="text"
            width="140px"
            height="20px"
            animation="wave"
          />
          <Skeleton
            variant="text"
            width="200px"
            height="20px"
            animation="wave"
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ margin: 0.5 }}>
              <Skeleton
                variant="circular"
                height="40px"
                width="40px"
                animation="wave"
              />
            </Box>
            <Box sx={{ margin: 0.5 }}>
              <Skeleton
                variant="circular"
                height="40px"
                width="40px"
                animation="wave"
              />
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Skeleton variant="text" height="30px" animation="wave" />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" height="30px" animation="wave" />
        </TableCell>
        {isPremiumOrg && (
          <TableCell>
            <Skeleton variant="text" height="60px" animation="wave" />
          </TableCell>
        )}
      </>
    );
  };

  const generateUserTransferTooltipTitle = () => {
    if (isEmaillessUser(row)) {
      return "Email-less users cannot be transferred";
    }
    if (permissions.isPermissionsLoading) {
      return "Loading permissions...";
    }
    if (!hasPermissions([ActionType.TRANSFER_USER_WITH_DOCUMENTS])) {
      return "You do not have permission to transfer users";
    }
    if (!row.multiOrgEnabled) {
      return "This user does not have multi-org enabled";
    }

    return "Transfer user with their documents";
  };

  return (
    <>
      <TableRow key={row.id}>
        {showSkeleton ? (
          renderSkeletonRow()
        ) : (
          <>
            {userStatusFilter !== UserStatus.UNSPECIFIED &&
              !adminOnlyFilter && (
                <TableCell padding="checkbox" style={{ width: "2.5%" }}>
                  <Checkbox
                    checked={selected}
                    onChange={(e) => setSelected(e.target.checked)}
                    inputProps={
                      {
                        "data-testid": `user-document-${row.id}-selected-input`,
                        // eslint-disable-next-line
                      } as any
                    }
                    data-testid={`user-document-${row.id}-selected`}
                  />
                </TableCell>
              )}
            <TableCell
              align="left"
              style={{ verticalAlign: "top", paddingLeft: "30px" }}
            >
              <Typography
                variant="body2"
                display="inline"
                color="primary"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                <Link
                  to={{
                    pathname: `/user/${regionCode}/${row.id}`,
                  }}
                  style={{ textDecoration: "none", color: "inherit" }}
                  data-testid="user-document-username"
                >
                  <b>
                    {row.firstName} {row.lastName}
                  </b>
                </Link>{" "}
              </Typography>
              <UserOrgsCountChip
                orgsCount={row.orgsCount}
                identityID={row.authId}
              />
              <br />
              <Typography
                variant="body2"
                display="inline"
                data-testid={"user-email"}
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                {row.email}
              </Typography>
              <br />
              <Box
                onClick={() => copyToClipboard(row.id)}
                sx={styles.clickable}
                display="inline"
                data-testid={"copy-to-clipboard"}
              >
                <Typography
                  variant="body2"
                  display="inline"
                  data-testid={"user-id"}
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-all",
                  }}
                >
                  {row.id}{" "}
                </Typography>
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={styles.positionIcon}
                />
              </Box>
            </TableCell>
            <TableCell>
              {row.permissionsSetIdentifier ? (
                <Tooltip
                  title={
                    "Permission set: " + row.permissionsSetIdentifier?.name
                  }
                >
                  <Button
                    onClick={() => {
                      setOpenPermissionSetsDialog(true);
                    }}
                    data-testid="permission-set-details-button"
                    style={{ padding: 0 }}
                  >
                    {truncateString(row.permissionsSetIdentifier?.name)}
                  </Button>
                </Tooltip>
              ) : null}
              <Box display="flex" data-testid={"privileges-icon-container"}>
                {row.privilegesEntries &&
                  showPrivilegeIcons(row.privilegesEntries)}
              </Box>
            </TableCell>
            <TableCell align="left">
              {userLastSeenDisplay(
                row.lastSeen && convertTimestampToDate(row.lastSeen),
              )}
            </TableCell>
            {isPremiumOrg && (
              <TableCell align="left">
                {subscriptionSeatTypesMapping[row.seatType]}
              </TableCell>
            )}
            <TableCell align="center">
              <Box display="flex" alignContent="center" justifyContent="center">
                <IconButton
                  onClick={handleActionMenuClick}
                  data-testid={"show-more-actions-button"}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  open={Boolean(actionMenuAnchor)}
                  anchorEl={actionMenuAnchor}
                  onClose={() => setActionMenuAnchor(null)}
                >
                  {!permissions.isPermissionsLoading &&
                    hasPermissions([ActionType.PROMOTE_USER_TO_ORG_ADMIN]) &&
                    row.seatType ==
                      SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM && (
                      <MenuItem
                        onClick={handlePromoteToAdminViaPermissionsDialog}
                        data-testid={"promote-to-admin-via-permissions-btn"}
                      >
                        Promote to Admin
                      </MenuItem>
                    )}

                  {isSubActive &&
                    isPremiumOrg &&
                    userStatusFilter === UserStatus.ACTIVE && (
                      <MenuItem
                        onClick={handleChangeSeatTypeDialog}
                        data-testid={"change-user-seat-type-btn"}
                      >
                        Change Seat Type
                      </MenuItem>
                    )}

                  {isSubActive && userStatusFilter === UserStatus.INACTIVE && (
                    <MenuItem
                      onClick={handleActivateDeactivateDialog}
                      data-testid={"activate-user-btn"}
                    >
                      Activate
                    </MenuItem>
                  )}
                  {isSubActive && (
                    <MenuItem
                      sx={{ color: "error.main" }}
                      onClick={handleRemoveUserDialog}
                      disabled={isRemoveUserDialogueDisabled}
                      data-testid={"remove-user-btn"}
                    >
                      Remove
                    </MenuItem>
                  )}
                  {
                    <Tooltip title={generateUserTransferTooltipTitle()}>
                      <span>
                        <MenuItem
                          sx={{ color: "error.main" }}
                          onClick={handleTransferUserDialog}
                          disabled={
                            isEmaillessUser(row) ||
                            isRemoveUserDialogueDisabled ||
                            permissions.isPermissionsLoading ||
                            !hasPermissions([
                              ActionType.TRANSFER_USER_WITH_DOCUMENTS,
                            ]) ||
                            !row.multiOrgEnabled
                          }
                          data-testid={"transfer-user-btn"}
                        >
                          Transfer
                        </MenuItem>
                      </span>
                    </Tooltip>
                  }
                  {isSubActive && userStatusFilter === UserStatus.ACTIVE && (
                    <MenuItem
                      sx={{ color: "error.main" }}
                      onClick={handleActivateDeactivateDialog}
                      /*disabled={isRemoveUserDialogueDisabled}*/
                      data-testid={"deactivate-user-btn"}
                    >
                      Deactivate
                    </MenuItem>
                  )}
                  <MenuItem
                    component={Link}
                    to={{
                      pathname: "/sudo",
                      state: {
                        regionCode: regionCode,
                        userId: row.id,
                        userName: `${row.firstName} ${row.lastName}`,
                        userEmail: row.email,
                      },
                    }}
                    sx={{ color: "secondary.main" }}
                    data-testid="sudo-menu-item"
                  >
                    SUDO
                  </MenuItem>
                </Menu>
              </Box>
            </TableCell>
          </>
        )}
      </TableRow>
      <ConfirmDialog
        title={`Remove ${row.firstName} ${row.lastName}`}
        open={openRemoveUserDialog}
        setOpen={setRemoveUserDialog}
        onConfirm={() => {
          handleRemoveUser(row.id, roleId, assigneeId);
        }}
        onCancel={() => {
          setAssigneeId("");
        }}
        disabled={disableRemoveUserConfirmBtn}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Remove"
      >
        <RemoveUser
          userId={row.id}
          userFirstName={row.firstName}
          userLastName={row.lastName}
          roleId={roleId}
          handleAssigneeId={(assigneeId: string) => setAssigneeId(assigneeId)}
          handleRemoveUserBtn={(isDisabled: boolean) =>
            setDisableRemoveUserConfirmBtn(isDisabled)
          }
        />
      </ConfirmDialog>
      <ConfirmDialog
        title={`Promote ${row.firstName} ${row.lastName} to Admin`}
        open={promoteToAdminViaPermissionsDialog}
        setOpen={setPromoteToAdminViaPermissionsDialog}
        onConfirm={promoteUserToAdminViaPermissions}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Promote"
      >
        You are about to promote the user {`"${row.firstName} ${row.lastName}"`}{" "}
        to have admin permissions within this org.
      </ConfirmDialog>
      <ChangeUserSeatTypeSelectedUsersModal
        open={changeSeatTypeDialog}
        setOpen={setChangeSeatTypeDialog}
        userIds={[row.id]}
        userSeatType={row.seatType}
        isPremiumOrg={isPremiumOrg}
        usersCurrentStatus={userStatusFilter}
        handleRefreshOrgDisplay={handleRefreshOrgDisplay}
        handleRefreshUserList={handleRefreshUserList}
        firstName={row.firstName}
        lastName={row.lastName}
      />
      <ActivateDeactivateSelectedUsersModal
        open={activateDeactivateDialog}
        setOpen={setActivateDeactivateDialog}
        setError={setErrorState}
        setFailedUsers={setFailedUsers}
        userIds={[row.id]}
        isPremiumOrg={isPremiumOrg}
        usersCurrentStatus={userStatusFilter}
        handleRefreshOrgDisplay={handleRefreshOrgDisplay}
        handleRefreshUserList={handleRefreshUserList}
        firstName={row.firstName}
        lastName={row.lastName}
      />
      <ConfirmDialog
        title={`Transfer ${row.firstName} ${row.lastName}`}
        open={openTransferUserDialog}
        setOpen={setTransferUserDialog}
        onConfirm={() => {
          handleTransferUser(
            row.id,
            roleId,
            regionCode,
            row.email,
            targetOrgId,
            targetSeatType,
          );
        }}
        onCancel={() => {
          setAssigneeId("");
        }}
        disabled={disableTransferUserConfirmBtn}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Transfer"
      >
        <TransferUser
          userFirstName={row.firstName}
          userLastName={row.lastName}
          sourceOrgId={roleId}
          handleAssigneeId={(assigneeId: string) => setAssigneeId(assigneeId)}
          handleTargetOrgId={(targetOrgId: string) =>
            setTargetOrgId(targetOrgId)
          }
          handleTargetSeatType={(targetSeatType: SubscriptionSeatTypes) =>
            setTargetSeatType(targetSeatType)
          }
          handleTransferUserBtn={(isDisabled: boolean) => {
            setDisableTransferUserConfirmBtn(isDisabled);
          }}
          seatType={row.seatType}
        />
      </ConfirmDialog>
      <GenericDialog
        title={row.permissionsSetIdentifier?.name + " - Permission Set Details"}
        width="xl"
        hasAckBtn={true}
        acknowledgeBtnLabel="OK"
        open={openPermissionSetsDialog}
        handleAckBtnClick={() => {
          setOpenPermissionSetsDialog(false);
        }}
        handleDialogClose={() => {
          setOpenPermissionSetsDialog(false);
        }}
        data-testid="permission-set-details-dialog"
      >
        <PermissionsDataGrid
          permissions={Object.entries(row.privilegesEntries)}
        />
      </GenericDialog>
      <GenericDialog
        title="Failed to deactivate user"
        width="sm"
        open={errorState}
        handleAckBtnClick={() => {
          setErrorState(false);
        }}
        handleDialogClose={() => {
          setErrorState(false);
        }}
        hasAckBtn={true}
        acknowledgeBtnLabel="OK"
      >
        Failed user: {failedUsers.join(", ")}
      </GenericDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={promotedToAdminSnackbar}
        onClose={() => setPromotedToAdminSnackbar(false)}
        data-testid="promote-to-admin-success"
      >
        <Alert severity="success" variant="filled">
          Successfully set user as admin, reload to see changes
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserRecord;
